import { useState, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { defaultProfileImage } from '../constants/defaultProfileImage';
import { getCroppedImg } from '../utils/cropImage';
import { playButtonClick } from '../utils/audio';

export default function LoginPage({
  darkMode,
  onDarkModeToggle,
  onGoogleLogin,
  onSetUsername,
  needsUsername,
  user,
  errorMessage,
  onBack
}) {
  const [username, setUsername] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [error, setError] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [storageBlocked, setStorageBlocked] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const previewImage = profileImage || user?.profileImage || defaultProfileImage;

  // Check storage accessibility on component mount
  useEffect(() => {
    if (!needsUsername) {
      checkStorageAccess();
    }
  }, [needsUsername]);

  const checkStorageAccess = () => {
    try {
      // Test both localStorage and sessionStorage - Firebase needs both
      localStorage.setItem('storage_test', 'test');
      localStorage.removeItem('storage_test');
      sessionStorage.setItem('storage_test', 'test');
      sessionStorage.removeItem('storage_test');
      setStorageBlocked(false);
      setShowStorageModal(false);
    } catch (e) {
      setStorageBlocked(true);
      setShowStorageModal(true);
    }
  };

  const bgStyle = { backgroundColor: '#1f2937' };
  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    marginBottom: '16px',
    ...(darkMode && { backgroundColor: '#374151', borderColor: '#4b5563', color: '#ffffff' })
  };
  const buttonStyle = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  };

  const handleSaveUsername = async () => {
    try {
      setError('');
      setIsStarting(true);
      await onSetUsername(username, previewImage);
    } catch (err) {
      setError(err.message || 'Failed to save username');
    } finally {
      setIsStarting(false);
    }
  };

  const compressDataUrl = (dataUrl) => new Promise((resolve) => {
    if (dataUrl.length <= 200000) {
      resolve(dataUrl);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const maxDim = 200;
      let { width, height } = img;
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);
      let q = 0.85;
      let compressed = canvas.toDataURL('image/jpeg', q);
      while (compressed.length > 200000 && q > 0.5) {
        q -= 0.05;
        compressed = canvas.toDataURL('image/jpeg', q);
      }
      resolve(compressed);
    };
    img.src = dataUrl;
  });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setCropImageSrc(dataUrl);
      setShowCropModal(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const applyCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(cropImageSrc, croppedAreaPixels, 200);
      const compressed = await compressDataUrl(cropped);
      setProfileImage(compressed);
      setShowCropModal(false);
    } catch (err) {
      console.error('Failed to crop image', err);
      setError('Failed to crop image');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      color: darkMode ? '#f9fafb' : '#111827'
    }}>
      {isStarting && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #374151',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#3b82f6',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Starting...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      <div style={{
        ...bgStyle,
        borderRadius: '16px',
        boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)',
        padding: '32px',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
        position: 'relative'
      }}>
        {needsUsername && (
          <button
            onClick={() => { playButtonClick(); onBack(); }}
            style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#1f2937',
              color: '#ffffff',
              border: '2px solid #374151',
              fontSize: '20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}
            aria-label="Back"
          >
            ←
          </button>
        )}
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', marginBottom: '8px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
          FitBudly
        </h1>
        <p style={{ color: '#f9fafb', marginBottom: '24px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
          Track workouts with friends
        </p>
        {!needsUsername && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {storageBlocked && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '2px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '8px'
              }}>
                <p style={{ color: '#dc2626', fontSize: '14px', fontWeight: 'bold', textShadow: 'none' }}>
                  ⚠️ Storage is blocked. Please enable cookies to sign in.
                </p>
                <button
                  onClick={() => { playButtonClick(); setShowStorageModal(true); }}
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Show Instructions
                </button>
              </div>
            )}
            
            <button
              onClick={storageBlocked ? () => setShowStorageModal(true) : () => { playButtonClick(); onGoogleLogin(); }}
              disabled={storageBlocked}
              style={{
                ...buttonStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: storageBlocked ? '#9ca3af' : '#f9fafb',
                color: '#111827',
                border: '2px solid #e5e7eb',
                cursor: storageBlocked ? 'not-allowed' : 'pointer',
                opacity: storageBlocked ? 0.6 : 1,
                textShadow: 'none'
              }}
            >
              <span style={{ fontSize: '18px' }}>🔑</span>
              Sign in with Google
            </button>
            
            {errorMessage && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '2px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px'
              }}>
                <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '8px', fontWeight: 'bold', textShadow: 'none' }}>
                  ⚠️ {errorMessage}
                </p>
                {errorMessage.includes('storage') && (
                  <p style={{ color: '#dc2626', fontSize: '12px', textShadow: 'none', textAlign: 'left' }}>
                    <strong>Troubleshooting:</strong><br/>
                    1. Enable cookies/site data in browser settings<br/>
                    2. Disable "Prevent Cross-Site Tracking" (Safari)<br/>
                    3. Try a different browser<br/>
                    4. Clear cache and reload
                  </p>
                )}
              </div>
            )}
            
            <p style={{ color: '#f9fafb', fontSize: '14px', marginTop: '12px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              We’ll ask for a unique username after you sign in.
            </p>
          </div>
        )}

        {needsUsername && (
          <div>
            <p style={{ color: '#f9fafb', marginBottom: '12px', fontSize: '14px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Choose a unique username so friends can find you.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Choose username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  ...inputStyle,
                  maxWidth: '300px',
                  textAlign: 'center'
                }}
              />
            </div>
            <div style={{
              border: '2px dashed',
              borderColor: darkMode ? '#4b5563' : '#d1d5db',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              marginBottom: '16px'
            }}>
              <label style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <img src={previewImage} alt="Profile" style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  margin: '0 auto'
                }} />
                <p style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '14px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                  {profileImage ? 'Change Photo' : 'Use default or upload'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            {error && (
              <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                {error}
              </p>
            )}
            {errorMessage && !error && (
              <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                {errorMessage}
              </p>
            )}
            <button
              onClick={() => { playButtonClick(); handleSaveUsername(); }}
              disabled={!username.trim() || isStarting}
              style={{
                ...buttonStyle,
                opacity: username.trim() && !isStarting ? 1 : 0.5,
                cursor: username.trim() && !isStarting ? 'pointer' : 'not-allowed',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              {isStarting ? 'Starting...' : 'Start Tracking'}
            </button>
          </div>
        )}
      </div>
      {showCropModal && (
        <div onClick={() => { playButtonClick(); setShowCropModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '16px', padding: '20px', width: '100%', maxWidth: '420px', textAlign: 'center' }}>
            <h3 style={{ color: '#f9fafb', marginBottom: '12px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Crop your photo</h3>
            <div style={{ position: 'relative', width: '100%', height: '260px', background: '#111827', borderRadius: '12px', overflow: 'hidden' }}>
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
              <button
                onClick={() => { playButtonClick(); setShowCropModal(false); }}
                style={{ padding: '10px 16px', backgroundColor: '#4b5563', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
              >
                Cancel
              </button>
              <button
                onClick={() => { playButtonClick(); applyCrop(); }}
                style={{ padding: '10px 16px', backgroundColor: '#3b82f6', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
              >
                Use Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Access Warning Modal */}
      {showStorageModal && (
        <div onClick={() => { playButtonClick(); setShowStorageModal(false); }} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#1f2937',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            border: '3px solid #ef4444',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '48px' }}>🍪</span>
            </div>
            <h3 style={{
              color: '#ef4444',
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '12px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Cookies & Storage Required
            </h3>
            <p style={{
              color: '#f9fafb',
              fontSize: '16px',
              marginBottom: '16px',
              lineHeight: '1.5',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              This app requires browser storage (cookies, localStorage, and sessionStorage) to function properly. Your browser currently has storage blocked.
            </p>
            <div style={{
              backgroundColor: '#374151',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              border: '2px solid #4b5563'
            }}>
              <p style={{
                color: '#fbbf24',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '8px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                To fix this:
              </p>
              <ul style={{
                color: '#f9fafb',
                fontSize: '14px',
                textAlign: 'left',
                lineHeight: '1.6',
                paddingLeft: '20px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}>
                <li><strong>Safari (iOS/Mac):</strong> Settings → Safari → Disable "Prevent Cross-Site Tracking" and "Block All Cookies"</li>
                <li><strong>Chrome (Android/Desktop):</strong> Settings → Privacy → Cookies → Allow all cookies</li>
                <li><strong>Firefox:</strong> Settings → Privacy → Standard or Custom (enable cookies)</li>
                <li><strong>Private/Incognito Mode:</strong> Storage is often blocked. Use a regular browser window instead</li>
                <li><strong>After changing settings:</strong> Force-close the browser and reopen</li>
              </ul>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { playButtonClick(); checkStorageAccess(); }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                ✓ Test Again
              </button>
              <button
                onClick={() => { playButtonClick(); setShowStorageModal(false); }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#6b7280',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                }}
              >
                Dismiss
              </button>
            </div>
            <p style={{
              color: '#9ca3af',
              fontSize: '12px',
              marginTop: '12px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              We use cookies only for authentication. No tracking.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}